import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, BellOff, ShoppingBag, ArrowRight, Volume2, VolumeX } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_MS = 12000;
const STORAGE_KEY = 'dashboard_new_orders_since';
const SOUND_PREF_KEY = 'dashboard_new_orders_sound';

const fmtINR = (n) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

/* ===========================================================
   CUSTOM CHIME
   - Loops /sounds/new-order.mp3 until the admin dismisses.
   - First user click "unlocks" the audio (muted play+pause trick)
     so the browser allows subsequent autoplay.
   - Falls back to a 3-tone Web Audio chime if the file is missing
     or play() is still rejected.
=========================================================== */
class CustomChime {
  constructor() {
    this.audio = new Audio('/sounds/new-order.mp3');
    this.audio.loop = true;
    this.audio.preload = 'auto';
    this.audio.volume = 0.9;
    this.audio.addEventListener('error', () => {
      this.fileBroken = true;
      console.warn('[chime] /sounds/new-order.mp3 failed to load — using synth fallback');
    });
    this.ctx = null;
    this.synthTimer = null;
    this.muted = false;
    this.unlocked = false;
    this.fileBroken = false;
  }

  /** Call from a real user gesture (click/tap) to satisfy autoplay policy. */
  async unlock() {
    if (this.unlocked || this.fileBroken) return;
    try {
      const wasMuted = this.audio.muted;
      this.audio.muted = true;
      await this.audio.play();
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio.muted = wasMuted;
      this.unlocked = true;
    } catch (err) {
      // No worries — the synth fallback also gets unlocked below.
    }
    try {
      if (!this.ctx) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) this.ctx = new Ctx();
      }
      if (this.ctx?.state === 'suspended') await this.ctx.resume();
    } catch (_) { /* ignore */ }
  }

  setMuted(m) {
    this.muted = m;
    if (m) this.stop();
  }

  async start() {
    if (this.muted) return;
    if (!this.fileBroken) {
      try {
        this.audio.currentTime = 0;
        await this.audio.play();
        return; // file is playing, we're done
      } catch (err) {
        console.warn('[chime] mp3 play() rejected, falling back to synth:', err?.message);
      }
    }
    this._startSynth();
  }

  stop() {
    try { this.audio.pause(); this.audio.currentTime = 0; } catch (_) {}
    if (this.synthTimer) {
      clearInterval(this.synthTimer);
      this.synthTimer = null;
    }
  }

  _startSynth() {
    try {
      if (!this.ctx) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        this.ctx = new Ctx();
      }
      if (this.ctx.state === 'suspended') this.ctx.resume();

      const playPattern = () => {
        if (this.muted) return;
        const now = this.ctx.currentTime;
        const tone = (freq, start, duration = 0.22, peak = 0.32) => {
          const osc = this.ctx.createOscillator();
          const gain = this.ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0, now + start);
          gain.gain.linearRampToValueAtTime(peak, now + start + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, now + start + duration);
          osc.connect(gain).connect(this.ctx.destination);
          osc.start(now + start);
          osc.stop(now + start + duration + 0.05);
        };
        tone(783.99, 0.0,  0.20);
        tone(1046.5, 0.20, 0.20);
        tone(1318.5, 0.40, 0.32);
      };
      playPattern();
      this.synthTimer = setInterval(playPattern, 2200);
    } catch (_) { /* ignore */ }
  }
}

export default function NewOrdersPopup() {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]); // unacknowledged orders, newest first
  const [enabled, setEnabled] = useState(true);
  const [soundOn, setSoundOn] = useState(
    () => localStorage.getItem(SOUND_PREF_KEY) !== 'off'
  );
  const sinceRef = useRef(localStorage.getItem(STORAGE_KEY) || null);
  const seenIdsRef = useRef(new Set());
  const chimeRef = useRef(null);
  const initializedRef = useRef(false);

  if (!chimeRef.current) chimeRef.current = new CustomChime();

  // Initial fetch — set baseline so we don't flood on first load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/dashboard/new-orders`);
        if (cancelled) return;
        const orders = r.data?.orders || [];
        for (const o of orders) seenIdsRef.current.add(o.id);
        const newest = orders[0]?.createdAt || r.data?.now || new Date().toISOString();
        sinceRef.current = newest;
        localStorage.setItem(STORAGE_KEY, newest);
      } finally {
        initializedRef.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Poll loop
  useEffect(() => {
    const poll = async () => {
      if (!initializedRef.current || !enabled) return;
      try {
        const r = await axios.get(`${API}/dashboard/new-orders`, {
          params: { since: sinceRef.current }
        });
        const orders = r.data?.orders || [];
        const fresh = orders.filter((o) => !seenIdsRef.current.has(o.id));
        if (fresh.length > 0) {
          for (const o of fresh) seenIdsRef.current.add(o.id);
          const newest = fresh[0].createdAt;
          sinceRef.current = newest;
          localStorage.setItem(STORAGE_KEY, newest);
          setQueue((prev) => [...fresh, ...prev]);
        }
      } catch (_) { /* ignore */ }
    };
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [enabled]);

  // Unlock audio on first user interaction (autoplay policy)
  useEffect(() => {
    const unlock = () => {
      chimeRef.current?.unlock();
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    window.addEventListener('touchstart', unlock, { once: true });
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, []);

  // Start/stop the loop based on queue
  useEffect(() => {
    chimeRef.current.setMuted(!soundOn);
    if (queue.length > 0 && soundOn) chimeRef.current.start();
    else chimeRef.current.stop();
  }, [queue.length, soundOn]);

  const current = queue[0];

  const dismissCurrent = () => setQueue((q) => q.slice(1));
  const dismissAll = () => { setQueue([]); chimeRef.current.stop(); };
  const toggleSound = () => {
    setSoundOn((v) => {
      const next = !v;
      localStorage.setItem(SOUND_PREF_KEY, next ? 'on' : 'off');
      return next;
    });
  };
  const openDetails = () => {
    if (!current) return;
    const id = current.id;
    dismissCurrent();
    chimeRef.current.stop();
    navigate(`/orders?open=${id}`);
  };

  return (
    <>
      {/* Toggle pill (live alerts on/off) */}
      <button
        type="button"
        onClick={() => setEnabled((v) => !v)}
        data-testid="dashboard-orders-alert-toggle"
        title={enabled ? 'Live order alerts ON' : 'Live order alerts OFF'}
        className={`fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium shadow-sm transition ${
          enabled
            ? 'bg-orange-600 text-white border-orange-600 hover:bg-orange-700'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
        }`}
      >
        {enabled ? <Bell size={14} /> : <BellOff size={14} />}
        {enabled ? 'Live alerts ON' : 'Live alerts OFF'}
      </button>

      {/* CENTER MODAL */}
      {current && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-[fadeIn_0.18s_ease-out]"
          data-testid="new-order-popup"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-orange-200 w-[440px] max-w-[95vw] overflow-hidden animate-[popIn_0.22s_ease-out]"
            style={{ fontFamily: 'Public Sans, sans-serif' }}
            data-testid={`new-order-modal-${current.id}`}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-5 py-3 flex items-center justify-between text-white">
              <div className="flex items-center gap-2.5">
                <div className="relative">
                  <ShoppingBag size={20} />
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-yellow-300 animate-ping" />
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-yellow-300" />
                </div>
                <span className="text-sm font-semibold tracking-wide">
                  NEW ORDER RECEIVED
                </span>
              </div>
              <button
                type="button"
                onClick={toggleSound}
                title={soundOn ? 'Mute sound' : 'Unmute sound'}
                data-testid="new-order-sound-toggle"
                className="p-1 rounded-md hover:bg-white/15 transition"
              >
                {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>
            </div>

            {/* Body */}
            <div className="p-5 text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                Order Number
              </p>
              <p
                className="text-2xl font-bold text-gray-900 mt-1 mb-3"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                #{current.orderNumber || current.id?.slice(-6)}
              </p>

              <div className="grid grid-cols-2 gap-3 text-left mb-4">
                <div className="rounded-lg bg-orange-50 border border-orange-200 p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-orange-700 font-semibold">
                    Restaurant
                  </p>
                  <p className="text-sm font-medium text-gray-900 truncate" title={current.restaurant?.name}>
                    {current.restaurant?.name || '—'}
                  </p>
                </div>
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-blue-700 font-semibold">
                    Customer
                  </p>
                  <p className="text-sm font-medium text-gray-900 truncate" title={current.user?.name}>
                    {current.user?.name || 'Guest'}
                  </p>
                  {current.user?.phone && (
                    <p className="text-[11px] text-gray-600">{current.user.phone}</p>
                  )}
                </div>
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">
                    Amount
                  </p>
                  <p className="text-base font-bold text-gray-900">
                    {fmtINR(current.grandTotal)}
                  </p>
                </div>
                <div className="rounded-lg bg-purple-50 border border-purple-200 p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-purple-700 font-semibold">
                    Method · Time
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {current.paymentMethod || '—'}
                  </p>
                  <p className="text-[11px] text-gray-600">
                    {new Date(current.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={dismissCurrent}
                  data-testid="new-order-dismiss-button"
                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={openDetails}
                  data-testid="new-order-details-button"
                  className="flex-[2] px-4 py-2.5 rounded-lg text-sm font-semibold text-white inline-flex items-center justify-center gap-1.5 shadow-md hover:shadow-lg transition"
                  style={{ backgroundColor: '#C2410C' }}
                >
                  Order Details <ArrowRight size={14} />
                </button>
              </div>

              {queue.length > 1 && (
                <button
                  type="button"
                  onClick={dismissAll}
                  className="mt-3 text-[11px] text-gray-500 hover:text-gray-800 underline"
                >
                  {queue.length - 1} more queued · dismiss all
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes popIn {
          from { transform: scale(0.92); opacity: 0; }
          to   { transform: scale(1);    opacity: 1; }
        }
      `}</style>
    </>
  );
}
